/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { gql, TypedDocumentNode } from '@apollo/client';
import {
    AuditLogFragment,
    CriticalVariableAuditFragment,
    IsmpFragment,
    NamedItemFragment,
    OpraPluginFragment,
    OrganisationFragment,
    OrganisationIndicationFragment,
    OrganisationMemberAuditFragment,
    OrganisationProductFragment,
    OrganisationProgramAuditFragment,
    OrganisationRoleFragment,
    PanelFragment,
    ReportFragment,
    ReportSignatoryFragment,
    RiskActionAuditFragment,
    RiskAuditFragment,
    RiskEvaluationModelFragment,
    RiskObservationAuditFragment,
    StudyAuditFragment,
    StudyCohortFragment,
    StudyCrfFragment,
    StudyControlAuditFragment,
    StudyMemberAuditFragment,
    StudySvtAuditFragment,
    StudyTreatmentArmFragment,
    SystemOrganisationFragment,
    UserAuditFragment,
    CentralMonitoringActionFragment,
    DataReviewTableFragment,
    DataSetFragment,
    DataSetReviewFragment,
    ExtraDataFieldFragment,
    StudyAssessmentFragment,
    CentralMonitoringObservationAuditFragment,
    ImportStateFragment,
    SiteReviewFragment,
    AuditLogChangeHistoryFragment,
    StudySubjectVisitFragment,
    LibraryRiskAuditFragment,
    LibraryControlAuditFragment,
    RiskEvaluationAuditFragment,
    PanelListFragment,
    DashboardAuditFragment,
    ReportDefinitionAuditFragment,
    AuditRecordIdPrefixFragment,
    DataViewFilterAuditFragment,
    DataViewConfigurationAuditFragment,
    DataViewFilterListFragment,
    StudyDashboardConfigurationAuditFragment,
    StudyDashboardConfigPanelAuditFragment,
    StudyDashboardConfigWidgetAuditFragment,
    WidgetAuditFragment,
    FunctionalDataReviewAuditFragment,
    LibraryFunctionalDataReviewsAuditFragment,
    ControlsHubWidgetAuditFragment,
    ControlsHubConfigWidgetAuditFragment,
    ControlsHubConfigPanelAuditFragment,
    DataViewConfigNamesFragment,
    CommentAuditFragment,
    CommentReplyAuditFragment,
} from './fragments';
import {
    addExportFEGridDataAudit,
    addExportFEGridDataAuditVariables,
    getLogoForAudit,
    getLogoForAuditVariables,
} from '@queries/generated/audit.generated';

export const SEARCH_AUDIT_LOGS = gql`
    query searchAuditLogs($conditions: [AuditSearchConditionInput]!) {
        searchAuditLogs(conditions: $conditions) {
            ...AuditLogFields            
        }
    }
    ${AuditLogFragment}
`;

export const AUDIT_LOGS_FOR_STUDY = gql`
    query auditLogsForStudy($studyId: ObjectId!, $startDate: DateTime!, $endDate: DateTime!) {
        auditLogsForStudy(studyId: $studyId, startDate: $startDate, endDate: $endDate) {
            ...AuditLogFields
        }
    }
    ${AuditLogFragment}
`;

export const AUDIT_RECORD_ID_PREFIXES = gql`
    query auditRecordIdPrefixes {
      auditRecordIdPrefixes {
          ...AuditRecordIdPrefixFields
      }
    }
    ${AuditRecordIdPrefixFragment}
`;

export const GET_AUDIT_HISTORY_CRITICAL_VARIABLE = gql`
    query criticalVariableHistory($id: ObjectId!) {
        criticalVariableHistory(id: $id) {
            ...CriticalVariableAuditFields
        }
    }
    ${CriticalVariableAuditFragment}
`;

export const GET_AUDIT_HISTORY_FUNCTIONAL_DATA_REVIEW = gql`
    query functionalDataReviewHistory($id: ObjectId!) {
        functionalDataReviewHistory(id: $id) {
            ...FunctionalDataReviewAuditFields
        }
    }
    ${FunctionalDataReviewAuditFragment}
`;

export const GET_AUDIT_HISTORY_RISK = gql`
    query riskHistory($id: ObjectId!) {
        riskHistory(id: $id) {
            ...RiskAuditFields
        }
    }
    ${RiskAuditFragment}
`;

export const GET_AUDIT_HISTORY_USER = gql`
    query userHistory($id: ObjectId!) {
        userHistory(id: $id) {
            ...UserAuditFields
        }
    }
    ${UserAuditFragment}
`;

export const GET_AUDIT_HISTORY_LIBRARY_CONTROL = gql`
    query libraryControlHistory($id: ObjectId!) {
        libraryControlHistory(id: $id) {
            ...LibraryControlAuditFields
        }
    }
    ${LibraryControlAuditFragment}
`;

export const GET_AUDIT_HISTORY_LIBRARY_RISK = gql`
    query libraryRiskHistory($id: ObjectId!) {
        libraryRiskHistory(id: $id) {
            ...LibraryRiskAuditFields
        }
    }
    ${LibraryRiskAuditFragment}
`;

export const GET_AUDIT_HISTORY_ISMP = gql`
    query ismpHistory($id: ObjectId!) {
        ismpHistory(id: $id) {
            ...IsmpFields
        }
    }
    ${IsmpFragment}
`;

export const GET_AUDIT_HISTORY_NAMED_ITEMS = gql`
    query namedItemHistory($id: ObjectId!) {
        namedItemHistory(id: $id) {
            ...NamedItemFields
        }
    }
    ${NamedItemFragment}
`;

export const GET_AUDIT_HISTORY_ORGANISATION = gql`
    query organisationHistory($id: ObjectId!) {
        organisationHistory(id: $id) {
            ...OrganisationFields
        }
    }
    ${OrganisationFragment}
`;

export const GET_AUDIT_HISTORY_SYSTEM_ORGANISATION = gql`
    query systemOrganisationHistory($id: ObjectId!) {
        systemOrganisationHistory(id: $id) {
            ...SystemOrganisationFields
        }
    }
    ${SystemOrganisationFragment}
`;

export const GET_AUDIT_HISTORY_ORGANISATION_ROLE = gql`
    query organisationRoleHistory($id: ObjectId!) {
        organisationRoleHistory(id: $id) {
            ...OrganisationRoleFields
        }
    }
    ${OrganisationRoleFragment}
`;

export const GET_AUDIT_HISTORY_PLUGIN = gql`
    query pluginHistory($id: ObjectId!) {
        pluginHistory(id: $id) {
            ...OpraPluginFields
        }
    }
    ${OpraPluginFragment}
`;

export const GET_AUDIT_HISTORY_PANEL = gql`
    query panelHistory($id: ObjectId!) {
        panelHistory(id: $id) {
            ...PanelFields
        }
    }
    ${PanelFragment}
`;

export const GET_AUDIT_HISTORY_STUDY = gql`
    query studyHistory($id: ObjectId!) {
        studyHistory(id: $id) {
            ...StudyAuditFields
        }
    }
    ${StudyAuditFragment}
`;

export const GET_AUDIT_HISTORY_SVT = gql`
    query studySubjectVisitScheduleTemplateHistory($id: ObjectId!) {
        studySubjectVisitScheduleTemplateHistory(id: $id) {
            ...StudySvtAuditFields
        }
    }
    ${StudySvtAuditFragment}
`;

export const GET_AUDIT_HISTORY_ORGANISATION_MEMBER = gql`
    query organisationMemberHistory($parentId: ObjectId!, $childId: ObjectId!) {
        organisationMemberHistory(parentId: $parentId, childId: $childId) {
            ...OrganisationMemberAuditFields
        }
    }
    ${OrganisationMemberAuditFragment}
`;

export const GET_AUDIT_HISTORY_EVALUATION_MODEL = gql`
    query riskEvaluationModelHistory($parentId: ObjectId!, $childId: ObjectId!) {
        riskEvaluationModelHistory(parentId: $parentId, childId: $childId) {
            ...RiskEvaluationModelFields
        }
    }
    ${RiskEvaluationModelFragment}
`;

export const GET_AUDIT_HISTORY_RISK_EVALUATION = gql`
    query riskEvaluationHistory($parentId: ObjectId!, $childId: ObjectId!) {
        riskEvaluationHistory(parentId: $parentId, childId: $childId) {
            ...RiskEvaluationAuditFields
        }
    }
    ${RiskEvaluationAuditFragment}
`;

export const GET_AUDIT_HISTORY_RISK_COMMENT = gql`
    query riskCommentHistory($id: ObjectId!) {
        riskCommentHistory(id: $id) {
            ...CommentAuditFields
        }
    }
    ${CommentAuditFragment}
`;

export const GET_AUDIT_HISTORY_RISK_COMMENT_REPLY = gql`
    query riskCommentReplyHistory($id: ObjectId!) {
        riskCommentReplyHistory(id: $id) {
            ...CommentReplyAuditFields
        }
    }
    ${CommentReplyAuditFragment}
`;

export const GET_AUDIT_HISTORY_CTQ_COMMENT = gql`
    query ctqCommentHistory($id: ObjectId!) {
        ctqCommentHistory(id: $id) {
            ...CommentAuditFields
        }
    }
    ${CommentAuditFragment}
`;

export const GET_AUDIT_HISTORY_CTQ_COMMENT_REPLY = gql`
    query ctqCommentReplyHistory($id: ObjectId!) {
        ctqCommentReplyHistory(id: $id) {
            ...CommentReplyAuditFields
        }
    }
    ${CommentReplyAuditFragment}
`;

export const GET_AUDIT_HISTORY_RISK_OBSERVATION = gql`
    query riskObservationHistory($id: ObjectId!) {
        riskObservationHistory(id: $id) {
            ...RiskObservationAuditFields
        }
    }
    ${RiskObservationAuditFragment}
`;

export const GET_AUDIT_HISTORY_RISK_ACTION = gql`
    query riskActionHistory($parentId: ObjectId!, $childId: ObjectId!) {
        riskActionHistory(parentId: $parentId, childId: $childId) {
            ...RiskActionAuditFields
        }
    }
    ${RiskActionAuditFragment}
`;

export const GET_AUDIT_HISTORY_STUDY_COHORT = gql`
    query studyCohortHistory($parentId: ObjectId!, $childId: ObjectId!) {
        studyCohortHistory(parentId: $parentId, childId: $childId) {
            ...StudyCohortFields
        }
    }
    ${StudyCohortFragment}
`;

export const GET_AUDIT_HISTORY_STUDY_CRF = gql`
    query studyCrfHistory($parentId: ObjectId!, $childId: ObjectId!) {
        studyCrfHistory(parentId: $parentId, childId: $childId) {
            ...StudyCrfFields
        }
    }
    ${StudyCrfFragment}
`;

export const GET_AUDIT_HISTORY_STUDY_MEMBER = gql`
    query studyMemberHistory($parentId: ObjectId!, $childId: ObjectId!) {
        studyMemberHistory(parentId: $parentId, childId: $childId) {
            ...StudyMemberAuditFields
        }
    }
    ${StudyMemberAuditFragment}
`;

export const GET_AUDIT_HISTORY_STUDY_TREATMENT_ARM = gql`
    query studyTreatmentArmHistory($parentId: ObjectId!, $childId: ObjectId!) {
        studyTreatmentArmHistory(parentId: $parentId, childId: $childId) {
            ...StudyTreatmentArmFields
        }
    }
    ${StudyTreatmentArmFragment}
`;

export const GET_AUDIT_HISTORY_STUDY_CONTROL = gql`
    query studyControlHistory($id: ObjectId!) {
        studyControlHistory(id: $id) {
            ...StudyControlAuditFields
        }
    }
    ${StudyControlAuditFragment}
`;

export const GET_AUDIT_HISTORY_ORGANISATION_PRODUCT = gql`
    query organisationProductHistory($parentId: ObjectId!, $childId: ObjectId!) {
        organisationProductHistory(parentId: $parentId, childId: $childId) {
            ...OrganisationProductFields
        }
    }
    ${OrganisationProductFragment}
`;

export const GET_AUDIT_HISTORY_ORGANISATION_PROGRAM = gql`
    query organisationProgramHistory($parentId: ObjectId!, $childId: ObjectId!) {
        organisationProgramHistory(parentId: $parentId, childId: $childId) {
            ...OrganisationProgramAuditFields
        }
    }
    ${OrganisationProgramAuditFragment}
`;

export const GET_AUDIT_HISTORY_ORGANISATION_INDICATION = gql`
    query organisationIndicationHistory($parentId: ObjectId!, $childId: ObjectId!) {
        organisationIndicationHistory(parentId: $parentId, childId: $childId) {
            ...OrganisationIndicationFields
        }
    }
    ${OrganisationIndicationFragment}
`;

export const GET_AUDIT_HISTORY_REPORT: any = gql`
    query reportHistory($id: ObjectId!) {
        reportHistory(id: $id) {
            ...ReportFields
        }
    }
    ${ReportFragment}
`;

export const GET_AUDIT_HISTORY_REPORT_SIGNATORY: any = gql`
    query reportSignatoryHistory($id: ObjectId!) {
        reportSignatoryHistory(id: $id) {
            ...ReportSignatoryFields
        }
    }
    ${ReportSignatoryFragment}
`;

export const GET_AUDIT_HISTORY_CM_OBSERVATION: any = gql`
    query centralMonitoringObservationHistory($id: ObjectId!) {
        centralMonitoringObservationHistory(id: $id) {
            ...CentralMonitoringObservationAuditFields
        }
    }
    ${CentralMonitoringObservationAuditFragment}
`;

export const GET_AUDIT_HISTORY_CM_ACTION: any = gql`
    query centralMonitoringActionHistory($parentId: ObjectId!, $childId: ObjectId!) {
        centralMonitoringActionHistory(parentId: $parentId, childId: $childId) {
            ...CentralMonitoringActionFields
        }
    }    
    ${CentralMonitoringActionFragment}
`;

export const GET_AUDIT_HISTORY_DATA_REVIEW_TABLE: any = gql`
    query dataReviewTableHistory($id: ObjectId!) {
        dataReviewTableHistory(id: $id) {
            ...DataReviewTableFields
        }
    }
    ${DataReviewTableFragment}
`;

export const GET_AUDIT_HISTORY_DATA_SET: any = gql`
    query dataSetHistory($id: ObjectId!) {
        dataSetHistory(id: $id) {
            ...DataSetFields
        }
    }
    ${DataSetFragment}
`;

export const GET_AUDIT_HISTORY_DATA_SET_REVIEW: any = gql`
    query dataSetReviewHistory($id: ObjectId!, $originService: AuditServiceType, $serviceItemId: Long) {
        dataSetReviewHistory(id: $id, originService: $originService, serviceItemId: $serviceItemId) {
            ...DataSetReviewFields
        }
    }
    ${DataSetReviewFragment}
`;

export const GET_AUDIT_HISTORY_EXTRA_DATA: any = gql`
    query extraDataHistory($id: ObjectId!) {
        extraDataHistory(id: $id) {
            ...ExtraDataFields
        }
    }
    ${ExtraDataFieldFragment}
`;

export const GET_AUDIT_HISTORY_IMPORT_STATE: any = gql`
    query importStateHistory($id: ObjectId!) {
        importStateHistory(id: $id) {
            ...ImportStateFields
        }
    }
    ${ImportStateFragment}
`;

export const GET_AUDIT_HISTORY_SITE_REVIEW: any = gql`
    query siteReviewHistory($parentId: ObjectId!, $childId: ObjectId!, $originService: AuditServiceType, $serviceItemId: Long) {
        siteReviewHistory(parentId: $parentId, childId: $childId, originService: $originService, serviceItemId: $serviceItemId ) {
            ...SiteReviewFields
        }
    }
    ${SiteReviewFragment}
`;

export const GET_AUDIT_LOG_CHANGE_HISTORY: any = gql`
    query auditLogChangeHistory($serviceItemId: Long) {
        auditLogChangeHistory(serviceItemId: $serviceItemId) {
            ...AuditLogChangeHistoryFields
        }
    }
    ${AuditLogChangeHistoryFragment}
`;

export const GET_AUDIT_HISTORY_STUDY_ASSESSMENT: any = gql`
    query studyAssessmentHistory($parentId: ObjectId!, $childId: ObjectId!) {
        studyAssessmentHistory(parentId: $parentId, childId: $childId) {
            ...StudyAssessmentFields
        }
    }
    ${StudyAssessmentFragment}
`;

export const GET_AUDIT_HISTORY_STUDY_SUBJECT_VISIT: any = gql`
    query studySubjectVisitHistory($parentId: ObjectId!, $childId: ObjectId!) {
        studySubjectVisitHistory(parentId: $parentId, childId: $childId) {
            ...StudySubjectVisitFields
        }
    }
    ${StudySubjectVisitFragment}
`;

export const GET_AUDIT_HISTORY_STUDY_DASHBOARD_SETUP: any = gql`
    query dashboardHistory($id: ObjectId!) {
        dashboardHistory(id: $id) {
            ...DashboardAuditFields
        }
    }
    ${DashboardAuditFragment}
`;

export const GET_AUDIT_HISTORY_PANELS_AND_TABLES: any = gql`
    query panelsAndTablesHistory($organisationId: ObjectId!, $studyId: ObjectId!, $timestamp: DateTime) {
        dataReviewTablesForStudy(studyId: $studyId) {
            ...DataReviewTableFields
        }
        panels(organisationId: $organisationId, studyId: $studyId, timestamp: $timestamp) {
            ...PanelListFields
        }
    }
    ${DataReviewTableFragment}
    ${PanelListFragment}
`;

export const GET_AUDIT_HISTORY_REPORT_DEFINITION: any = gql`
    query reportDefinitionHistory($id: ObjectId!) {
        reportDefinitionHistory(id: $id) {
            ...ReportDefinitionAuditFields
        }
    }
    ${ReportDefinitionAuditFragment}
`;

export const GET_AUDIT_HISTORY_DATA_VIEW_CONFIGURATION: any = gql`
    query dataViewConfigurationHistory($id: ObjectId!) {
        dataViewConfigurationHistory(id: $id) {
            ...DataViewConfigurationAuditFields
        }
    }
    ${DataViewConfigurationAuditFragment}
`;

export const GET_AUDIT_HISTORY_DATA_VIEW_FILTER: any = gql`
    query dataViewFilterHistory($id: ObjectId!) {
        dataViewFilterHistory(id: $id) {
            ...DataViewFilterAuditFields
        }
    }
    ${DataViewFilterAuditFragment}
`;

export const GET_AUDIT_HISTORY_PANELS_AND_TABLES_AND_FILTERS: any = gql`
    query panelsAndTablesAndFiltersHistory($organisationId: ObjectId!, $studyId: ObjectId!, $timestamp: DateTime) {
        dataReviewTablesForStudy(studyId: $studyId, timestamp: $timestamp) {
            ...DataReviewTableFields
        }
        panels(organisationId: $organisationId, studyId: $studyId, timestamp: $timestamp) {
            ...PanelListFields
        }
        dataViewDataFilters(studyId: $studyId) {
            ...DataViewFilterListFields
        }
    }
    ${DataReviewTableFragment}
    ${PanelListFragment}
    ${DataViewFilterListFragment}
`;

export const GET_AUDIT_HISTORY_STUDY_DASHBOARD_CONFIG: any = gql`
    query studyDashboardConfigurationHistory($id: ObjectId!) {
        studyDashboardConfigurationHistory(id: $id) {
            ...StudyDashboardConfigurationAuditFields
        }
}
    ${StudyDashboardConfigurationAuditFragment}
    `;

export const GET_AUDIT_HISTORY_STUDY_DASHBOARD_CONFIG_PANEL: any = gql`
    query studyDashboardConfigPanelHistory($id: ObjectId!) {
        studyDashboardConfigPanelHistory(id: $id) {
            ...StudyDashboardConfigPanelAuditFields
        }
    }
    ${StudyDashboardConfigPanelAuditFragment}
`;

export const GET_AUDIT_HISTORY_STUDY_DASHBOARD_CONFIG_WIDGET: any = gql`
    query studyDashboardConfigWidgetHistory($id: ObjectId!) {
        studyDashboardConfigWidgetHistory(id: $id) {
            ...StudyDashboardConfigWidgetAuditFields
        }
    }
    ${StudyDashboardConfigWidgetAuditFragment}
`;

export const GET_AUDIT_HISTORY_WIDGET: any = gql`
    query widgetHistory($id: ObjectId!) {
        widgetHistory(id: $id) {
            ...WidgetAuditFields
        }
    }
    ${WidgetAuditFragment}
`;

export const GET_AUDIT_HISTORY_LIBRARY_FUNCTIONAL_DATA_REVIEW: any = gql`
    query libraryFunctionalDataReviewHistory($id: ObjectId!) {
        libraryFunctionalDataReviewHistory(id: $id) {
            ...LibraryFunctionalDataReviewAuditFields
        }
    }
    ${LibraryFunctionalDataReviewsAuditFragment}
`;

export const ADD_EXPORT_FE_GRID_DATA_AUDIT: TypedDocumentNode<
    addExportFEGridDataAudit,
    addExportFEGridDataAuditVariables
> = gql`
    mutation addExportFEGridDataAudit(
        $studyId: ObjectId!,
        $filename: String!,
        $service: AuditServiceType!,
        $area: AuditApplicationArea!,
        $dataType: AuditDataType!)
    {
        addExportFEGridDataAudit(studyId: $studyId, filename: $filename, service: $service, area: $area, dataType: $dataType) {
            success,
            reason
        }
    }
`;

export const GET_AUDIT_HISTORY_CONTROLS_HUB_CONFIG_WIDGET: any = gql`
    query controlsHubConfigWidgetHistory($id: ObjectId!) {
        controlsHubConfigWidgetHistory(id: $id) {
            ...ControlsHubConfigWidgetAuditFields
        }
    }
    ${ControlsHubConfigWidgetAuditFragment}
`;

export const GET_AUDIT_HISTORY_CONTROLS_HUB_WIDGET: any = gql`
    query controlsHubWidgetHistory($id: ObjectId!) {
        controlsHubWidgetHistory(id: $id) {
            ...ControlsHubWidgetAuditFields
        }
    }
    ${ControlsHubWidgetAuditFragment}
`;

export const GET_AUDIT_HISTORY_CONTROLS_HUB_CONFIG_PANEL: any = gql`
    query controlsHubConfigPanelHistory($id: ObjectId!) {
        controlsHubConfigPanelHistory(id: $id) {
            ...ControlsHubConfigPanelAuditFields
        }
    }
    ${ControlsHubConfigPanelAuditFragment}
`;


export const GET_AUDIT_HISTORY_PANELS_AND_DATA_VIEWS: any = gql`
    query panelsAndDataViewsHistory($organisationId: ObjectId!, $studyId: ObjectId!, $timestamp: DateTime) {
        panels(organisationId: $organisationId, studyId: $studyId, timestamp: $timestamp) {
            ...PanelListFields
        }
        dataViewConfigurations(studyId: $studyId, timestamp: $timestamp) {
            ...DataViewConfigNames
        }
    }
    ${PanelListFragment}
    ${DataViewConfigNamesFragment}
`;

export const GET_LOGO_FOR_AUDIT: TypedDocumentNode<getLogoForAudit, getLogoForAuditVariables> = gql`
    query getLogoForAudit($logoId: ObjectId!) {
        getLogoForAudit(logoId: $logoId) {
            id,
            created,
            logoData
        }
    }
`;
